<template>
  <div v-if="invalidToken">
    <forbidden-page></forbidden-page>
  </div>
  <div v-else>
    <b-loading
      :is-full-page="true"
      :active.sync="isLoading"
      :can-cancel="false"
    >
      <div class="loading-wrap">
        <div class="loading-icon"></div>
        <p class="loading-text">{{ loadingStateText || $t('loading') }}</p>
      </div>
    </b-loading>
    <div class="task-container">
      <b-modal
        :active="isAssemblyOverviewModalActive"
        @close="() => (isAssemblyOverviewModalActive = false)"
      >
        <img :src="getAssemblyOverview()" />
      </b-modal>

      <Header
        ref="header"
        :tool="tool"
        :current-phase-id="
          tabPhases[currentPhase] ? tabPhases[currentPhase]._id : null
        "
        @sidebarOpened="() => (isSidebarOpen = true)"
        @sidebarClose="() => (isSidebarOpen = false)"
      />
      <div
        class="task-items-outer"
        :class="isSidebarOpen ? 'sidebar-open' : ''"
      >
        <section style="margin-top: 10px; margin-left: 10px">
          <b-tabs
            type="is-boxed"
            v-model="currentPhase"
            :animated="false"
            :key="phaseTabsKey"
          >
            <b-tab-item
              :label="phase.name"
              v-for="phase in tabPhases"
              :key="phase._id || 'all'"
            >
              <template #header>
                <span>{{ phase.name }}</span>
                <b-tag rounded style="margin-left: 6px">
                  {{ getPhaseCompletness(phase.completenessPercentage) }}%
                </b-tag>
              </template>
            </b-tab-item>
          </b-tabs>
        </section>
        <div
          :class="'task-list' + (job.jobCompleted ? ' completed-distance' : '')"
        >
          <div
            class="addTask"
            v-if="
              !isOrderTasksActive &&
              !isMultiMoveActive &&
              tool === 'editor' &&
              !readOnly &&
              canMutateCurrentJob
            "
            @click="addTask"
          >
            <div class="addTaskIcon"></div>
            <span>{{ $t('task_page.create_new_task') }}</span>
          </div>

          <div
            v-if="
              ((job.assemblyOverviewUrl && tool === 'inspector') ||
                tool === 'publisher') &&
              this.folderId == ''
            "
            class="task-item assembly-overview"
          >
            <b-upload
              v-if="!job.assemblyOverviewUrl && tool === 'publisher'"
              v-model="assemblyOverviewFile"
              drag-drop
              accept="image/*,application/pdf"
              :disabled="!canMutateCurrentJob"
            >
              <section class="section">
                <div class="has-text-centered">
                  <p>
                    <b-icon icon="upload" size="is-large"> </b-icon>
                  </p>
                  <p>{{ $t('task_page.drop_file_reference_image') }}</p>
                </div>
              </section>
            </b-upload>
            <div
              v-else
              style="
                display: flex;
                flex-direction: column;
                height: 100%;
                align-items: center;
                width: 100%;
              "
            >
              <div style="height: 196px; width: 100%; display: flex">
                <b-button
                  v-if="tool !== 'inspector'"
                  type="is-danger"
                  icon-right="delete"
                  style="position: absolute; margin: 5px"
                  @click="deleteAssemblyOverview"
                  :disabled="!canMutateCurrentJob"
                />
                <div
                  style="
                    height: 196px;
                    width: 100%;
                    display: flex;
                    justify-content: center;
                  "
                >
                  <img
                    :src="getAssemblyOverview()"
                    style="max-height: 220px; object-fit: contain"
                    @click="() => (isAssemblyOverviewModalActive = true)"
                  />
                </div>
              </div>
              <p>{{ $t('task_page.reference_image') }}</p>
            </div>
          </div>

          <draggable
            v-model="tasks"
            :disabled="!isOrderTasksActive"
            :class="!isOrderTasksActive ? 'list-group' : 'drag-list-group'"
            ghost-class="ghost"
            @start="dragging = true"
            @end="dragging = false"
          >
            <div
              v-for="task in getTasks"
              :class="computeTaskItemClass(task, isOrderTasksActive)"
              v-bind:key="task._id"
              :title="
                !computedIsOffline && !task.hasAllData
                  ? ''
                  : $t('task_page.task_not_available_offline')
              "
            >
              <div class="selector" v-if="isMultiMoveActive">
                <input
                  :class="'selector-input selector-for-' + task._id"
                  type="checkbox"
                  @click="toggleTaskSelection(task)"
                />
              </div>
              <div
                class="thumbnail"
                @click="toggleTaskSelection(task)"
                v-if="isMultiMoveActive"
              >
                <img class="task-img" :src="getTaskThumbnail(task)" />
              </div>
              <div
                class="thumbnail"
                @click="goToTask(task)"
                v-if="!isMultiMoveActive"
              >
                <div class="task-img-icon">
                  <img class="img-icon" :src="getTaskIcon(task)" />
                </div>
                <img class="task-img" :src="getTaskThumbnail(task)" />
              </div>
              <div :class="completenessClass(task)">
                <div class="note-inner" style="display: inline-grid">
                  <b-tooltip
                    type="is-light"
                    :label="task.taskName"
                    position="is-bottom"
                  >
                    <div class="note-inner-task-name">{{ task.taskName }}</div>

                    <div
                      class="note-options"
                      v-if="!tool.includes('inspector')"
                    >
                      <b-dropdown
                        aria-role="list"
                        position="is-top-left"
                        append-to-body
                        v-if="!isMultiMoveActive && !isOrderTasksActive"
                      >
                        <b-icon
                          class="options-icon"
                          slot="trigger"
                          role="button"
                          :icon="'dots-horizontal'"
                        ></b-icon>

                        <b-dropdown-item
                          aria-role="listitem"
                          :disabled="!canMutateCurrentJob"
                          @click="editFolder(task)"
                          v-if="task.taskType === 'folder'"
                          ><b-icon icon="pencil"></b-icon
                          >{{ $t('edit') }}</b-dropdown-item
                        >

                        <b-dropdown-item
                          aria-role="listitem"
                          :disabled="!canMutateCurrentJob"
                          @click="duplicateFolder(task)"
                          v-if="task.taskType === 'folder'"
                          ><b-icon icon="content-duplicate"></b-icon>
                          {{ $t('duplicate') }}</b-dropdown-item
                        >

                        <b-dropdown-item
                          aria-role="listitem"
                          :disabled="!canMutateCurrentJob"
                          @click="moveToFolder(task)"
                          v-if="
                            task.taskType === 'folder' && !task.hasSubfolder
                          "
                          ><b-icon icon="file-move"></b-icon>
                          {{ $t('move_folder') }}</b-dropdown-item
                        >

                        <b-dropdown-item
                          aria-role="listitem"
                          :disabled="!canMutateCurrentJob"
                          @click="editTaskName(task)"
                          v-if="task.taskType !== 'folder'"
                          ><b-icon icon="pencil"></b-icon
                          >{{ $t('edit_name') }}</b-dropdown-item
                        >
                        <b-dropdown-item
                          aria-role="listitem"
                          :disabled="!canMutateCurrentJob"
                          @click="deleteTask(task)"
                          ><b-icon icon="delete"></b-icon
                          >{{ $t('delete') }}</b-dropdown-item
                        >
                        <b-dropdown-item
                          aria-role="listitem"
                          :disabled="!canMutateCurrentJob"
                          @click="duplicate(task)"
                          v-if="task.taskType !== 'folder'"
                          ><b-icon icon="content-duplicate"></b-icon>
                          {{ $t('duplicate') }}</b-dropdown-item
                        >
                        <b-dropdown-item
                          aria-role="listitem"
                          :disabled="!canMutateCurrentJob"
                          @click="moveToFolder(task)"
                          v-if="task.taskType !== 'folder'"
                          ><b-icon icon="file-move"></b-icon>
                          {{ $t('move_folder') }}</b-dropdown-item
                        >
                        <b-dropdown-item
                          aria-role="listitem"
                          @click="openChangePhase(task)"
                          v-if="projectPhases && projectPhases.length > 1"
                          ><b-icon icon="swap-horizontal"></b-icon>
                          {{ $t('task_page.change_phase') }}</b-dropdown-item
                        >
                        <b-dropdown-item
                          v-if="
                            isTaskCompleted(task) && task.taskType !== 'folder'
                          "
                          aria-role="listitem"
                          :disabled="!canMutateCurrentJob"
                          @click="reopen(task)"
                          ><b-icon icon="eye"></b-icon>
                          {{ $t('reopen') }}</b-dropdown-item
                        >
                      </b-dropdown>
                    </div>
                  </b-tooltip>

                  <div class="diagram-icons" v-if="task.taskType === 'diagram'">
                    <div
                      v-for="icon in task.measurementsTypes || []"
                      v-bind:key="icon"
                      :class="'icon-img icon-' + icon"
                      :title="icon.charAt(0).toUpperCase() + icon.slice(1)"
                    ></div>
                    <b-tag
                      v-if="tool === 'publisher' || tool === 'inspector'"
                      rounded
                      style="margin-left: auto"
                    >
                      <b-tooltip
                        type="is-light"
                        :label="
                          $t('task_page.signed_off_by', { name: task.signedBy })
                        "
                        position="is-bottom"
                        v-if="isTaskCompleted(task)"
                      >
                        <b-icon icon="check" type="is-success"></b-icon>
                      </b-tooltip>

                      <span class="icon-completeness-ipad" v-else
                        >{{
                          parseFloat(task.completenessPercentage || 0).toFixed(
                            2
                          )
                        }}
                        %</span
                      >
                    </b-tag>
                  </div>
                  <b-tag
                    v-if="
                      task.taskType !== 'diagram' &&
                      (tool === 'publisher' || tool === 'inspector')
                    "
                    style="margin-left: auto"
                    rounded
                  >
                    <b-tooltip
                      type="is-light"
                      :label="'Signed Off by ' + task.signedBy"
                      position="is-bottom"
                      v-if="isTaskCompleted(task)"
                    >
                      <b-icon icon="check" type="is-success"></b-icon>
                    </b-tooltip>

                    <span v-else
                      >{{
                        parseFloat(task.completenessPercentage || 0).toFixed(2)
                      }}
                      %</span
                    ></b-tag
                  >
                </div>
              </div>
            </div>
          </draggable>
        </div>
      </div>

      <b-modal
        :active.sync="isEditNameActive"
        has-modal-card
        trap-focus
        aria-role="dialog"
        aria-modal
        class="edit-name-modal"
      >
        <div class="modal-card" style="width: auto">
          <header class="modal-card-head">
            <p class="modal-card-title">
              {{ $t('edit') }} {{ editTask ? editTask.taskName : '' }}
            </p>
          </header>
          <section class="modal-card-body">
            <md-field>
              <label>{{ $t('create_task.task_name') }}</label>
              <md-input required v-model="editTask.taskName"></md-input>
            </md-field>
          </section>
          <footer class="modal-card-foot">
            <button class="" type="button" @click="isEditNameActive = false">
              {{ $t('close') }}
            </button>
            <button class="" @click="saveTaskName()">{{ $t('save') }}</button>
          </footer>
        </div>
      </b-modal>
      <b-modal
        :active.sync="isMoveTaskActive"
        :width="640"
        scroll="keep"
        :can-cancel="['escape', 'outside']"
      >
        <header class="modal-card-head">
          <p class="modal-card-title">
            {{ $t('task_page.select_folder_to_move') }}
          </p>
        </header>
        <section class="modal-card-body">
          <b-button
            v-for="folder in foldersToMove"
            :icon-left="folder._id === '' ? 'folder-remove' : 'folder'"
            :key="folder._id"
            size="is-large"
            class="folder-button"
            @click="moveTask(folder)"
          >
            {{ folder.taskName }}
          </b-button>
        </section>
        <footer class="modal-card-foot">
          <button style="width: 100%" type="button" @click="cancelMoveTask()">
            {{ $t('close') }}
          </button>
        </footer>
      </b-modal>

      <b-modal
        :active.sync="isChangePhaseActive"
        :width="640"
        scroll="keep"
        :can-cancel="['escape', 'outside']"
      >
        <header class="modal-card-head">
          <p class="modal-card-title">
            {{ $t('task_page.select_phase_to_move') }}
          </p>
        </header>
        <section class="modal-card-body">
          <b-button
            v-if="currentTabPhaseId !== null"
            icon-left="close-circle-outline"
            size="is-large"
            class="folder-button"
            @click="changeTaskPhase({ _id: null })"
          >
            All (no phase)
          </b-button>
          <b-button
            v-for="phase in selectablePhases"
            :key="phase._id"
            icon-left="swap-horizontal"
            size="is-large"
            class="folder-button"
            @click="changeTaskPhase(phase)"
          >
            {{ phase.name }}
          </b-button>
        </section>
        <footer class="modal-card-foot">
          <button
            style="width: 100%"
            type="button"
            @click="cancelChangePhase()"
          >
            {{ $t('close') }}
          </button>
        </footer>
      </b-modal>

      <div class="floating-button" style="display: none">
        <img
          :src="require('@/assets/se_repair.png')"
          @click="openChatBox"
          @mouseenter="showTooltip = true"
          @mouseleave="showTooltip = false"
          class="clickable-img"
          alt="Help Icon"
        />
        <div v-show="showTooltip" class="tooltip">
          {{ $t('task_page.need_help_chat') }}
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import draggable from 'vuedraggable';
import jwt_decode from 'jwt-decode';
import { getDocument } from 'pdfjs-dist';
import { v4 } from 'uuid';
import 'pdfjs-dist/webpack.mjs';
import Header from '../layout/TasksHeader';
import KeyHandlerMixin from '../../mixins/KeyHandlerMixin';
import { getRecordsUsingIndex } from '../../services/db';
import OfflineMixin from '../../mixins/OfflineMixin';
import FoldersManagerMixin from '../../mixins/FoldersManagerMixin';
import DIJVueConfigurations from '../../helpers/DIJVueConfigurations';
import { compareByTaskOrder } from '../../helpers/taskOrder';
import { GET_CURRENT_JOB } from '../../store/jobs/getters/getterTypes';

const uuidv4 = v4;

export default {
  name: 'task-page',
  components: { Header, draggable },
  mixins: [KeyHandlerMixin, OfflineMixin, FoldersManagerMixin],
  data() {
    return {
      isSidebarOpen: false,
      showTooltip: false,
      jobId: null,
      foldersToMove: [],
      isMoveTaskActive: false,
      invalidToken: false,
      tokenData: null,
      isOrderTasksActive: false,
      isMultiMoveActive: false,
      isEditNameActive: false,
      editTask: { taskName: '' },
      showCompletenessTooltip: true,
      readOnly: false,
      isLoading: false,
      tasks: [],
      selectedTasks: [],
      unwatchJob: null,
      unwatchTasks: null,
      prevFolder: null,
      hasSubfolderID: null,
      assemblyOverviewFile: null,
      isAssemblyOverviewModalActive: false,
      loadingStateText: null,
      isDeleting: false,
      isCancelling: false,
      isChangingPhase: false,
      currentPhase: 0,
      isChangePhaseActive: false,
      taskToChangePhase: null,
      isMounting: false,
    };
  },

  props: {
    tool: String,
  },
  watch: {
    tasks(newValue) {
      this.$store.dispatch('setUpdatedTasks', newValue);
    },
    async currentPhase(newPhase) {
      if (this.isMounting) return;
      const phase = this.tabPhases[newPhase];
      if (!phase) return;

      const { id, folderId, subfolderId } = this.$route.params;

      // Persist the selected phase so it is restored after navigation.
      localStorage.setItem(`dij_phase_${id}`, newPhase);

      // When switching phase from inside a folder, go back to the job root
      // so the user sees all root-level tasks for the selected phase. At the
      // root, just reload tasks for the new phase as before.
      if (folderId || subfolderId) {
        this.$router.push(this.getJobViewRoute(id));
        return;
      }

      const options = { jobId: id };
      if (phase._id) options.jobPhaseId = phase._id;

      this.isLoading = true;
      try {
        await this.$store.dispatch('getJobTasks', options);

        this.tasks = [...this.$store.state.tasks].sort((a, b) =>
          a.taskOrder > b.taskOrder ? 1 : -1
        );
      } finally {
        this.isLoading = false;
      }
    },
    computedIsOffline() {
      this.$router.go(0);
    },
    isEditNameActive(isActive) {
      this.setKeyHandlerCallback('update', isActive ? this.saveTaskName : null);
    },
    async assemblyOverviewFile(file) {
      try {
        this.isLoading = true;
        if (file.type === 'application/pdf') {
          await this.savePdfToImageS3(file);
        } else {
          await this.saveImageToS3(file);
        }
      } catch (error) {
        console.error(error);
      } finally {
        this.isLoading = false;
      }
    },
    isLoading(isLoading) {
      if (!isLoading) {
        this.loadingStateText = null;
      }
    },
  },
  computed: {
    getTasks() {
      return this.tasks;
    },
    job() {
      return this.$store.state.currentJob;
    },
    currentJob() {
      return this.$store.getters[GET_CURRENT_JOB];
    },
    user() {
      return this.$store.state.currentUser;
    },
    userRoles() {
      return this.$store.state.userRoles;
    },
    sites() {
      return this.$store.state.sites;
    },
    projectPhases() {
      const currentSite = this.sites.find(
        (site) => site._id === this.job.executingSite
      );

      if (!currentSite?.jobPhases?.length) return [];

      const jobPhases = (this.currentJob.jobPhases || []).map((p) =>
        typeof p === 'string' ? { _id: p } : p
      );
      const jobPhaseIds = new Set(jobPhases.map((p) => p._id));
      const progressById = new Map(
        jobPhases.map((p) => [p._id, p.completenessPercentage || 0])
      );

      return currentSite.jobPhases
        .filter((p) => p.isDefault || jobPhaseIds.has(p._id))
        .map((p) => ({
          ...p,
          completenessPercentage: progressById.get(p._id) || 0,
        }));
    },
    tabPhases() {
      const allTab = {
        _id: null,
        name: 'ALL',
        completenessPercentage: this.currentJob?.completenessPercentage || 0,
      };
      if (this.projectPhases.length > 0) return [allTab, ...this.projectPhases];
      return [allTab];
    },
    phaseTabsKey() {
      return this.tabPhases.map((p) => p._id || 'all').join('|');
    },

    currentTabPhaseId() {
      return this.tabPhases[this.currentPhase]?._id ?? null;
    },
    selectablePhases() {
      return this.projectPhases.filter(
        (phase) => phase._id !== this.currentTabPhaseId
      );
    },
    canMutateCurrentJob() {
      if (!this.job || !this.tool) {
        return false;
      }

      return this.$store.getters.canMutateTask(this.job, this.tool, {
        isReadOnly: this.readOnly,
        allowLegacyNoPermissions: true,
      });
    },
  },
  created() {
    this.unwatchJob = this.$store.watch(
      () => {
        return this.$store.state.currentJob;
      },
      (updatedJob) => {
        if (updatedJob.approvalStatus === 'requested') {
          this.readOnly = true;
        }
        if (updatedJob) {
          this.$forceUpdate();
        }
      },
      {
        deep: true,
      }
    );
  },

  async mounted() {
    this.isLoading = true;
    this.isMounting = true;
    // fetch the job by its id on the route
    const { id, folderId, subfolderId } = this.$route.params;
    this.jobId = id;

    if (this.jobId && this.userRoles.length === 0) {
      this.loadingStateText = this.$t('task_page.loading_user_roles');
      await this.$store.dispatch('getUserRoles');
    }

    this.loadingStateText = this.$t('task_page.loading_job_details');
    const { currentJob } = this.$store.state;
    if (!currentJob || currentJob._id !== id) {
      await this.$store.dispatch('getJob', id);
    }

    this.$store.commit('SET_PROGRESS_BAR', {
      key: 'job-loading',
      value: 60,
      message: 'Loading job details...',
    });

    try {
      this.loadingStateText = this.$t('task_page.checking_job_status');
      if (
        this.currentJob &&
        this.currentJob.checkedOut &&
        this.currentJob.checkedOut !== this.user.id // Check also the copy of the id because for offline the user is taken from the session->user
      ) {
        this.$router.push({
          path: '/view/dashboard',
          query: { offline: true },
        });
      }

      if (!this.sites || this.sites.length === 0) {
        await this.$store.dispatch('getSites');
      }

      const queryPhaseId = this.$route.query.phaseId;
      const queryPhaseIndex = queryPhaseId
        ? this.tabPhases.findIndex((phase) => phase._id === queryPhaseId)
        : -1;
      if (queryPhaseIndex > -1) {
        this.currentPhase = queryPhaseIndex;
        localStorage.setItem(`dij_phase_${id}`, queryPhaseIndex);
      } else {
        const savedPhaseIndex = parseInt(
          localStorage.getItem(`dij_phase_${id}`),
          10
        );
        if (
          !isNaN(savedPhaseIndex) &&
          savedPhaseIndex < this.tabPhases.length
        ) {
          this.currentPhase = savedPhaseIndex;
        }
      }

      const isValidFolderRoute = await this.validateFolderRoute(
        id,
        folderId,
        subfolderId
      );

      if (!isValidFolderRoute) {
        this.isLoading = false;
        return false;
      }

      const selectedPhase = this.tabPhases[this.currentPhase];
      const options = { jobId: id };
      if (selectedPhase && selectedPhase._id)
        options.jobPhaseId = selectedPhase._id;
      if (folderId) options.folderId = folderId;
      if (subfolderId) options.folderId = subfolderId;
      this.prevFolder = folderId;
      this.hasSubfolderID = subfolderId;

      this.loadingStateText = this.$t('task_page.loading_job_tasks');
      this.$store.commit('SET_PROGRESS_BAR', {
        key: 'job-loading',
        value: 75,
        message: this.loadingStateText,
      });
      await this.$store.dispatch('getJobTasks', options);

      this.loadingStateText = this.$t('task_page.preparing_tasks');
      this.$store.commit('SET_PROGRESS_BAR', {
        key: 'job-loading',
        value: 90,
        message: this.loadingStateText,
      });

      this.tasks = [...this.$store.state.tasks].sort(compareByTaskOrder);

      this.unwatchTasks = this.$store.watch(
        () => {
          return this.$store.state.tasks;
        },
        (newTasks) => {
          this.tasks = newTasks;
          this.$forceUpdate();
        }
      );
    } catch (error) {
      console.error(error);
      this.$router.push({
        name: '404',
      });
      this.isLoading = false;
      this.$store.commit('SET_PROGRESS_BAR', {
        key: 'job-loading',
        value: null,
        message: '',
      });
      return false;
    } finally {
      this.isMounting = false;
    }

    if (this.job && this.job.childConfig && this.job.childConfig.length === 0) {
      const newChild = [];
      this.job.childConfig.push(newChild);
    }

    const { token } = this.$route.query;
    if (token) {
      this.tokenData = await jwt_decode(token, process.env.JWT_SECRET);
      this.readOnly = this.tokenData.action === 'view';
      await this.checkToken(token);
      await this.hideIfToken();

      if (this.tool === 'editor') {
        this.checkApprovalStatus();
      }

      if (this.tool === 'editor' && this.job.type !== 'productLibrary') {
        this.$router.push({
          name: '404',
        });
      }

      if (
        (this.tool === 'publisher' || this.tool === 'inspector') &&
        this.job.type !== 'workViewerLibrary' &&
        this.job.type !== 'workOrderLibrary'
      ) {
        this.$router.push({
          name: '404',
        });
      }
    }

    // Check if the user is online and cache JobData for connection/wi-fi lost use case

    // TODO: Re-enable for wifi disruptions
    // if (
    //   this.tool !== 'editor' &&
    //   (await onlineService.getStatus()) &&
    //   !folderId &&
    //   !subfolderId
    // ) {
    //   this.cacheJobData();
    // }

    this.$store.commit('SET_TASK_OBJECT', {});
    this.isLoading = false;

    this.$store.commit('SET_PROGRESS_BAR', {
      key: 'job-loading',
      value: 100,
      message: 'Job loaded',
    });

    setTimeout(() => {
      this.$store.commit('SET_PROGRESS_BAR', {
        key: 'job-loading',
        value: null,
      });
    }, 800);

    this.$nextTick(() => {
      this.$watch('$refs.header.isOrderTasksActive', async (newValue) => {
        this.isOrderTasksActive = newValue;
      });

      this.$watch('$refs.header.deleteTasks', async () => {
        this.isDeleting = true;
        this.isLoading = true;
        await this.deleteTasks();
        this.isMultiMoveActive = false;
        this.isLoading = false;
        this.isDeleting = false;
      });

      this.$watch('$refs.header.cancelTasks', async () => {
        this.isCancelling = true;
        this.isMoveTaskActive = false;
        this.isMultiMoveActive = false;
        this.selectedTasks = [];
        this.isCancelling = false;
      });

      this.$watch('$refs.header.isMultiMoveActive', async (newValue) => {
        if (this.isDeleting || this.isCancelling || this.isChangingPhase)
          return;
        // If the mode is disabled enable moveTask
        if (newValue) {
          this.isMultiMoveActive = newValue;
        } else {
          this.moveToFolder();
        }
      });

      this.$watch('$refs.header.changePhaseForSelected', () => {
        this.isChangePhaseActive = true;
      });

      this.$watch('$refs.header.isMultiSelect', async (newValue) => {
        const tasksToSelect = this.tasks.filter(
          (item) => item.taskType !== 'folder'
        );
        if (newValue) {
          tasksToSelect.forEach((task) => {
            document.querySelector(`.selector-for-${task._id}`).checked = true;
            this.selectedTasks.push(task);
          });
        } else {
          tasksToSelect.forEach((task) => {
            document.querySelector(`.selector-for-${task._id}`).checked = false;
            this.selectedTasks = [];
          });
        }
      });

      this.$watch('$refs.header.updateJob', async (newValue) => {
        this.isLoading = true;
        if (newValue) {
          // await this.updateThumbnail();
          await this.$store.dispatch('updateJobSimple', this.job);
        }
        this.isLoading = false;
      });
    });

    if (this.tool === 'editor') {
      if (!this.$store.getters.canEditJob(this.job, this.tool)) {
        this.readOnly = true;
      }

      return false;
    }
    if (this.computedIsOffline) {
      const tasksClone = this.tasks.map((task) => ({ ...task }));

      const results = await Promise.all(
        tasksClone.map(async (task) => {
          if (task.taskType === 'folder') {
            return { ...task, hasAllData: true };
          }

          const [bubbles, questions] = await Promise.all([
            getRecordsUsingIndex('bubble', 'taskId', task._id),
            getRecordsUsingIndex('question', 'taskId', task._id),
          ]);

          return {
            ...task,
            hasAllData: bubbles.length > 0 || questions.length > 0,
          };
        })
      );

      this.tasks = results;
    }
  },
  beforeDestroy() {
    if (this.unwatchJob) this.unwatchJob();
    if (this.unwatchTasks) this.unwatchTasks();
  },
  methods: {
    getPhaseCompletness(completenessPercentage) {
      if (completenessPercentage) {
        return parseFloat(completenessPercentage || 0).toFixed(2);
      }
      return 0;
    },

    getJobViewRoute(jobId) {
      const route = {
        path: `/view/${this.tool}/${jobId}`,
      };

      if (this.$route.query.token) {
        route.query = { token: this.$route.query.token };
      }

      return route;
    },
    async validateFolderRoute(jobId, folderId, subfolderId) {
      const routeFolderId = subfolderId || folderId;

      if (!routeFolderId) {
        return true;
      }

      try {
        const routeFolder = await this.$store.dispatch('getTask', {
          id: routeFolderId,
        });

        if (!routeFolder || routeFolder.taskType !== 'folder') {
          await this.$router.replace(this.getJobViewRoute(jobId));
          this.$store.commit('SET_PROGRESS_BAR', {
            key: 'job-loading',
            value: null,
            message: '',
          });
          return false;
        }

        if (routeFolder.jobId !== jobId) {
          await this.$router.replace(this.getJobViewRoute(jobId));
          this.$store.commit('SET_PROGRESS_BAR', {
            key: 'job-loading',
            value: null,
            message: '',
          });
          return false;
        }

        const activePhaseId = this.currentTabPhaseId;
        const folderPhaseId = routeFolder.jobPhaseId || null;
        if (activePhaseId && folderPhaseId && folderPhaseId !== activePhaseId) {
          const targetIndex = this.tabPhases.findIndex(
            (phase) => phase._id === folderPhaseId
          );
          if (targetIndex > -1) {
            this.currentPhase = targetIndex;
            localStorage.setItem(`dij_phase_${jobId}`, targetIndex);
          }
          await this.$router.replace(this.getJobViewRoute(jobId));
          this.$store.commit('SET_PROGRESS_BAR', {
            key: 'job-loading',
            value: null,
            message: '',
          });
          return false;
        }

        if (subfolderId && folderId && routeFolder.folderId !== folderId) {
          await this.$router.replace(this.getJobViewRoute(jobId));
          this.$store.commit('SET_PROGRESS_BAR', {
            key: 'job-loading',
            value: null,
            message: '',
          });
          return false;
        }

        return true;
      } catch (error) {
        console.error('Invalid folder route', error);
        await this.$router.replace(this.getJobViewRoute(jobId));
        this.$store.commit('SET_PROGRESS_BAR', {
          key: 'job-loading',
          value: null,
          message: '',
        });
        return false;
      }
    },
    async saveImageToS3(file) {
      const dijConfigurations = new DIJVueConfigurations();
      const url = await dijConfigurations.uploadFileToS3(
        file,
        file.name,
        `uploads/products/${this.jobId}`
      );

      const newJob = { ...this.job };
      newJob.assemblyOverviewUrl = url;
      newJob.thumbnailUrl = url;
      await this.$store.dispatch('updateJobSimple', newJob);
    },

    async savePdfToImageS3(file) {
      const dataURL = await this.handlePDF(file);

      const byteString = atob(dataURL.split(',')[1]);
      const mimeString = dataURL.split(',')[0].split(':')[1].split(';')[0];
      const byteNumbers = new Array(byteString.length);

      for (let j = 0; j < byteString.length; j++) {
        byteNumbers[j] = byteString.charCodeAt(j);
      }

      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], { type: mimeString });
      const fileJsonName = `${uuidv4()}_pdf.png`;
      const fileJson = new File([blob], fileJsonName);
      const dijConfigurations = new DIJVueConfigurations();
      const url = await dijConfigurations.uploadFileToS3(
        fileJson,
        fileJsonName,
        `uploads/products/${this.jobId}`
      );
      const newJob = { ...this.job };
      newJob.assemblyOverviewUrl = url;
      newJob.thumbnailUrl = url;
      await this.$store.dispatch('updateJobSimple', newJob);
    },

    async deleteAssemblyOverview() {
      try {
        this.isLoading = true;
        const newJob = { ...this.job };
        newJob.assemblyOverviewUrl = null;
        await this.$store.dispatch('updateJobSimple', newJob);
      } catch (error) {
        console.error('Error deleting assembly overview:', error);
      } finally {
        this.isLoading = false;
      }
    },

    openChatBox() {
      this.$chatbox.open({
        title: 'Pubbl-i',
        message: 'How can I help?',
      });
    },
    computeTaskItemClass(task, isOrderTasksActive) {
      let classes = 'task-item';

      if (isOrderTasksActive) {
        classes += ' task-order';
      }

      if (task.taskType === 'folder') {
        classes += ' folder folder-clip-path';
      }

      if (this.computedIsOffline && !task.hasAllData) {
        classes += ' task-disabled';
      }

      return classes;
    },
    isTaskCompleted(task) {
      return task.workStatusCode
        ? task.workStatusCode.toString() === '2'
        : false;
    },
    completenessClass(task) {
      if (
        this.isTaskCompleted(task) &&
        (this.tool === 'publisher' || this.tool === 'inspector')
      ) {
        return 'note-complete';
      }
      if (
        task.completenessPercentage !== 0 &&
        (this.tool === 'publisher' || this.tool === 'inspector')
      ) {
        return 'note-medium';
      }

      return 'note-begin';
    },

    async hideIfToken() {
      const arrowBackButton = document.getElementsByClassName('back-button');
      const menuNavbar = document.getElementsByClassName('navbar-item');
      const headerLogo = document.getElementsByClassName('header-logo');

      const gotoProductListButton = document.getElementById('gotoProductList');
      const elementsToHide = document.getElementsByClassName(
        'header-inner headerTool'
      );
      const breadcrumb = document.getElementsByClassName('breadcrumb');
      if (breadcrumb && breadcrumb.length > 0) {
        const arrowPath = breadcrumb[0].getElementsByTagName('pre');
        breadcrumb[0].style = 'margin-left: 10% !important';
        arrowPath[0].style.display = 'none';
      }

      if (gotoProductListButton) gotoProductListButton.style.display = 'none';

      if (arrowBackButton && arrowBackButton.length > 0)
        arrowBackButton[0].style.display = 'none';

      Array.from(elementsToHide).forEach((element) => {
        element.style.display = 'none';
      });

      if (menuNavbar && menuNavbar.length > 0)
        menuNavbar[0].style.display = 'none';

      if (headerLogo && headerLogo.length > 0) headerLogo[0].href = '';
    },

    async checkToken(token) {
      try {
        this.tokenData = await jwt_decode(token, process.env.JWT_SECRET);
        this.readOnly = this.tokenData.action === 'view';

        const currentTime = Date.now() / 1000;
        if (currentTime > this.tokenData.exp) {
          this.invalidToken = true;
          this.$router.push('/logout');
        }
        if (this.tokenData.action === 'view') {
          this.readOnly = true;
        }
      } catch (err) {
        this.invalidToken = true;
        this.$router.push('/logout');
      }
    },
    async getJobFolder(jobId, folderId, taskFolderId) {
      const options = {
        jobId,
        folderId,
        return: true,
      };
      this.isLoading = true;
      const folders = await this.$store.dispatch('getJobTasks', options);

      folders.forEach((folder) => {
        if (folder.taskType === 'folder') {
          if (folder._id !== taskFolderId) this.foldersToMove.push(folder);
        }
      });
      this.isLoading = false;
    },
    getTaskIcon(task) {
      if (task.taskType === 'diagram') {
        return '/img/diagram_icon.png';
      }
      if (task.taskType === 'instruction') {
        return '/img/Instruction_icon.png';
      }
    },
    getTaskThumbnail(task) {
      if (!task || typeof task !== 'object') {
        console.warn('Invalid task object provided');
        return '/img/image-empty.png';
      }

      if (task.taskType === 'diagram') {
        if (task.diagram?.src && typeof task.diagram.src === 'string') {
          const parts = task.diagram.src.split('/');
          if (parts.length < 2) {
            console.warn(`Invalid diagram source path: ${task.diagram.src}`);
            return '/img/image-empty.png';
          }

          const lastPart = parts.pop();
          const allButLast = parts.join('/');

          const imageSource = `${allButLast}/${encodeURIComponent(lastPart)}`;

          // Handle local S3 environment
          return window.location.host.includes('localhost')
            ? `http://localhost:4566/images${imageSource}`
            : imageSource;
        }

        console.warn('task.diagram.src is missing or not a string', task);
        return '/img/image-empty.png';
      }

      if (task.taskType === 'instruction') {
        if (task.thumbnailUrl) {
          return window.location.host.includes('localhost')
            ? `http://localhost:4566/images${task.thumbnailUrl}`
            : task.thumbnailUrl;
        }
        return '/img/ic-assignment-black-24-px.png';
      }

      // If it's a folder, return an empty string (no default image)
      if (task.taskType === 'folder') {
        return '';
      }

      return '/img/image-empty.png';
    },

    getAssemblyOverview() {
      return window.location.host.includes('localhost')
        ? `http://localhost:4566/images${this.job.assemblyOverviewUrl}`
        : this.job.assemblyOverviewUrl;
    },

    goToTask(task) {
      const taskId = task._id;
      const jobId = task.jobId || this.currentJob._id;

      this.$store.commit('SET_PROGRESS_BAR', {
        key: 'job-loading',
        value: 0,
        message: 'Loading task...',
      });
      this.isLoading = true;
      if (this.$route.query.token) {
        const { token } = this.$route.query;

        if (task.taskType === 'folder' && task.folderId) {
          return this.$router.push({
            path: `/view/${this.tool}/${jobId}/folder/${task.folderId}/${task.taskNumber}`,
            query: { token },
          });
        }

        if (task.taskType === 'folder') {
          return this.$router.push({
            path: `/view/${this.tool}/${jobId}/folder/${taskId}`,
            query: { token },
          });
        }

        this.$router.push({
          name: `${this.tool}-template`, // editor-template || publisher || inspector
          params: { id: jobId, taskNumber: taskId },
          query: { token },
        });
      } else {
        if (task.taskType === 'folder' && task.folderId) {
          return this.$router.push({
            path: `/view/${this.tool}/${jobId}/folder/${task.folderId}/${task.taskNumber}`,
          });
        }

        if (task.taskType === 'folder') {
          return this.$router.push({
            path: `/view/${this.tool}/${jobId}/folder/${taskId}`,
          });
        }
        // Removed becouse now we should let user to see their work but not to change it
        // if (this.job.jobCompleted && this.tool === 'inspector') {
        //   return;
        // }

        this.isLoading = false;

        this.$router.push({
          name: `${this.tool}-template`,
          params: { id: jobId, taskNumber: taskId },
        });
      }
    },

    async editTaskName(task) {
      if (await this.preventOfflineAction()) {
        return;
      }
      this.isEditNameActive = true;
      this.editTask = { ...task };
    },

    async editFolder(folderTask) {
      if (await this.preventOfflineAction()) {
        return;
      }
      // eslint-disable-next-line no-nested-ternary
      const routeTool = this.tool.includes('editor')
        ? 'editor'
        : this.tool.includes('publisher')
        ? 'publisher'
        : '';

      const url = {
        name: 'create-folder',
        params: { id: this.job._id, module: routeTool },
        query: { edit: true },
      };

      if (this.$route.query.token) {
        url.query.token = this.$route.query.token;
      }

      if (folderTask.folderId) {
        url.query.subfolderId = folderTask._id;
      } else {
        url.query.folderId = folderTask._id;
      }

      this.$router.push(url);
    },
    async saveTaskName() {
      const updatedTask = this.editTask;
      if (!updatedTask || !updatedTask._id) {
        return;
      }
      if (
        updatedTask.childConfig &&
        updatedTask.childConfig[0] &&
        updatedTask.childConfig[0].taskName
      ) {
        updatedTask.childConfig[0].taskName = updatedTask.taskName;
      }

      const taskIndex = this.tasks.findIndex(
        (task) => task._id === updatedTask._id
      );
      this.tasks[taskIndex] = updatedTask;
      try {
        this.isLoading = true;
        await this.$store.dispatch('updateTask', updatedTask);
        this.isEditNameActive = false;
        this.isLoading = false;
      } catch (e) {
        this.$store.dispatch('setErrorMessage', e.message);
        this.isLoading = false;
      }
    },

    async reopen(task) {
      if (await this.preventOfflineAction()) {
        return;
      }
      try {
        this.editTask = { ...task };
        this.editTask.workStatusCode = 1;
        const updatedTask = this.editTask;
        updatedTask.signedBy = null;
        updatedTask.signedAt = null;
        const taskIndex = this.tasks.findIndex(
          (taskFind) => taskFind._id === updatedTask._id
        );
        this.tasks[taskIndex] = updatedTask;
        await this.$store.dispatch('updateTask', updatedTask);
        await this.$store.dispatch('reopenJob', this.job._id);
      } catch (e) {
        this.$store.dispatch('setErrorMessage', e.message);
      }
    },

    async duplicateFolder(folder) {
      this.isLoading = true;
      this.loadingStateText = this.$t('task_page.duplicating_folder');
      if (await this.preventOfflineAction()) {
        this.isLoading = false;
        return;
      }

      await this.$store.dispatch('duplicateFolder', folder);
      this.isLoading = false;
    },

    async duplicate(task) {
      if (await this.preventOfflineAction()) {
        return;
      }
      const clearedTask = { ...task };
      clearedTask.baseTaskId = task._id;
      await this.$store.dispatch('duplicateTask', clearedTask);
    },

    cancelMoveTask() {
      this.isMoveTaskActive = false;
      this.isMultiMoveActive = false;
      this.selectedTasks = [];
    },

    openChangePhase(task) {
      this.taskToChangePhase = task;
      this.isChangePhaseActive = true;
    },

    cancelChangePhase() {
      this.isChangePhaseActive = false;
      this.taskToChangePhase = null;
    },

    async changeTaskPhase(phase) {
      const isOnPhaseTab = this.tabPhases[this.currentPhase]?._id !== null;
      const isBulk = this.selectedTasks.length > 0;
      const items = isBulk ? this.selectedTasks : [this.taskToChangePhase];
      try {
        await Promise.all(items.map((t) => this.applyPhaseChange(t, phase)));
        // The item leaves the current list when the phase tab changed, or when
        // it was a nested item that has now been moved out to the project root.
        items.forEach((t) => {
          if (isOnPhaseTab || t.folderId) {
            const idx = this.tasks.findIndex((item) => item._id === t._id);
            if (idx !== -1) this.tasks.splice(idx, 1);
          }
        });
        if (isBulk) {
          this.isChangingPhase = true;
          this.$refs.header.isMultiMoveActive = false;
          this.isChangingPhase = false;
          this.isMultiMoveActive = false;
          this.selectedTasks = [];
        }
      } catch (e) {
        this.$store.dispatch('setErrorMessage', e.message);
      } finally {
        this.isChangePhaseActive = false;
        this.taskToChangePhase = null;
      }
    },

    async applyPhaseChange(t, phase) {
      const task = { ...t, jobPhaseId: phase._id };
      // A nested task/folder moves to the project root in the selected phase,
      // because its parent folder stays in the old phase and would otherwise
      // orphan it.
      if (t.folderId) {
        task.folderId = null;
        task.folderLevel = 0;
        task.folderTitle = null;
      }
      await this.$store.dispatch('updateTask', task);
      // A folder carries its whole subtree into the new phase so its contents
      // stay consistent with the folder itself.
      if (t.taskType === 'folder') {
        const descendants = await this.collectDescendants(t.jobId, t._id);
        await Promise.all(
          descendants.map((child) =>
            this.$store.dispatch('updateTask', {
              ...child,
              jobPhaseId: phase._id,
            })
          )
        );
      }
    },

    async collectDescendants(jobId, folderId) {
      const children = await this.$store.dispatch('getJobTasks', {
        jobId,
        folderId,
        return: true,
      });
      const nested = await Promise.all(
        children
          .filter((child) => child.taskType === 'folder')
          .map((child) => this.collectDescendants(jobId, child._id))
      );
      return children.concat(...nested);
    },

    async moveTask(folder) {
      try {
        await this.$store.dispatch('moveTasks', {
          tasks: this.selectedTasks,
          folder,
        });
        this.selectedTasks.forEach((selectedTask) => {
          selectedTask.folderId = folder._id;
          this.tasks.splice(
            this.tasks.findIndex((task) => task._id === selectedTask._id),
            1
          );
        });
      } catch (e) {
        this.$store.dispatch('setErrorMessage', e.message);
      } finally {
        this.isMoveTaskActive = false;
        this.isMultiMoveActive = false;
        this.selectedTasks = [];
      }
    },

    async moveToFolder(task) {
      if (await this.preventOfflineAction()) {
        return;
      }
      if (task) {
        // Support the previous file copy method
        this.toggleTaskSelection(task);
      } else if (this.selectedTasks.length !== 0) {
        [task] = this.selectedTasks;
      } else {
        // There's no file selected
        this.cancelMoveTask();
        return;
      }
      if (this.foldersToMove.length === 0) {
        // folders and tasks list
        if (task.folderId) {
          this.tasks.forEach((folder) => {
            if (folder.taskType === 'folder' && folder._id !== task._id) {
              this.foldersToMove.push(folder);
            }
          });
          this.getJobFolder(task.jobId, null, task.folderId);
          if (this.hasSubfolderID) {
            this.foldersToMove.push({
              _id: this.prevFolder,
              taskName: 'Root',
            });
            this.getJobFolder(task.jobId, this.prevFolder, task.folderId);
          } else {
            this.foldersToMove.push({ _id: '', taskName: 'Root' });
          }
        } else {
          // inside a folder, only tasks list
          // TODO get folders from job other than what we are in
          this.getJobFolder(task.jobId, null, null);
        }
      }
      this.isMoveTaskActive = true;
    },

    async deleteTasks() {
      if (await this.preventOfflineAction()) {
        return;
      }
      const message = this.$t('task_page.delete_confirmation');
      this.$buefy.dialog.confirm({
        message,
        type: 'is-dark',
        hasIcon: true,
        icon: 'delete',
        confirmText: 'Delete',
        onConfirm: async () => {
          try {
            // delete the task
            this.isLoading = true;
            this.loadingStateText = this.$t('task_page.deleting_task');

            const deletionPromises = this.selectedTasks.map((taskId) => {
              const taskIndex = this.tasks.findIndex(
                (item) => item._id === taskId._id
              );
              if (taskIndex !== -1) {
                return this.$store.dispatch(
                  'deleteTask',
                  this.tasks[taskIndex]._id
                );
              }
            });
            await Promise.all(deletionPromises);
            this.isLoading = false;

            this.$store.dispatch('getJobDeletedTasks', { jobId: this.jobId });
            // Update taskOrder of the remaining tasks
            this.$store.dispatch('updateTasksOrder', {
              jobId: this.job._id,
            });

            // remove the task from jobArray
            // this.tasks.splice(taskIndex, 1);
          } catch (e) {
            this.$store.dispatch('setErrorMessage', e.message);
            this.isLoading = false;
          }
        },
      });
    },

    async deleteTask(task) {
      if (await this.preventOfflineAction()) {
        return;
      }
      const number = task._id;
      let message = this.$t('task_page.delete_confirmation');
      if (task.taskType === 'folder') {
        message = this.$t('task_page.delete_folder_confirmation_message');
      }

      this.$buefy.dialog.confirm({
        message,
        type: 'is-dark',
        hasIcon: true,
        icon: 'delete',
        confirmText: 'Delete',
        onConfirm: async () => {
          try {
            // get the index of the task to be removed
            const taskIndex = this.tasks.findIndex(
              (item) => item._id === number
            );
            // delete the task
            this.isLoading = true;
            this.loadingStateText = this.$t('task_page.deleting_task');
            await this.$store.dispatch('deleteTask', this.tasks[taskIndex]._id);
            this.isLoading = false;

            this.$store.dispatch('getJobDeletedTasks', { jobId: this.jobId });
            // Update taskOrder of the remaining tasks
            this.$store.dispatch('updateTasksOrder', {
              jobId: this.job._id,
            });

            // remove the task from jobArray
            // this.tasks.splice(taskIndex, 1);
          } catch (e) {
            this.$store.dispatch('setErrorMessage', e.message);
            this.isLoading = false;
          }
        },
      });
    },

    addTask() {
      const currentPhase = this.tabPhases[this.currentPhase];
      const query = {};
      if (this.$route.query.token) query.token = this.$route.query.token;
      if (currentPhase && currentPhase._id) query.phaseId = currentPhase._id;
      this.$router.push({
        name: `${this.tool}-create-task`,
        params: { id: this.job._id },
        query: Object.keys(query).length ? query : undefined,
      });
    },

    verifyAccess(job) {
      const hasAccessToExecutingSite = this.user.secondaryLocations.some(
        (site) => site === job.executingSite
      );

      if (
        job.prodCreatedByUser &&
        (this.user.privil === 1 ||
          this.user.location === job.prodCreatedByUser.location ||
          this.user.location === job.executingSite ||
          job.sites.some((site) => site === this.user.location) ||
          hasAccessToExecutingSite)
      ) {
        return true;
      }

      return false;
    },
    getJobId(job) {
      if (job.prodCreatedByUser && job.prodCreatedByUser._id) {
        return job.prodCreatedByUser._id;
      }
      return '';
    },
    checkApprovalStatus() {
      if (
        this.job?.approvalStatus === 'approved' ||
        this.job?.approvalStatus === 'requested'
      ) {
        this.readOnly = true;
      }
    },
    toggleTaskSelection(task) {
      const foundTaskIndex = this.selectedTasks.findIndex(
        (selectedTask) => selectedTask._id === task._id
      );

      if (foundTaskIndex === -1) {
        this.selectedTasks.push(task);
        if (this.isMultiMoveActive) {
          document.querySelector(`.selector-for-${task._id}`).checked = true;
        }
      } else {
        this.selectedTasks.splice(foundTaskIndex, 1);
        if (this.isMultiMoveActive) {
          document.querySelector(`.selector-for-${task._id}`).checked = false;
        }
      }
    },
    async updateFolderCompleteness(folder) {
      const folderId = folder._id;
      let count = 0;
      let tasksLength = 0;
      let workStatusCode = 2;
      const tasks = await this.$store.dispatch('getJobTasks', {
        jobId: this.job._id,
        folderId,
        return: true,
      });
      tasks.forEach((task) => {
        let value;
        tasksLength++;
        if (typeof task.completenessPercentage === 'string') {
          if (task.completenessPercentage.includes('%')) {
            value = parseInt(task.completenessPercentage.split('%')[0], 10);
          } else {
            value = parseInt(task.completenessPercentage, 10);
          }
        } else {
          value = task.completenessPercentage;
        }
        if (task.workStatusCode !== 2) {
          workStatusCode = 1;
        }
        count += value;
      });
      if (tasksLength === 0) return false;
      const percentageCompleteness = (count / tasksLength).toFixed(0);
      folder.completenessPercentage = `${percentageCompleteness}`;
      folder.workStatusCode = workStatusCode;
      await this.$store.dispatch('updateTask', folder);
    },
    async updateJobCompleteness() {
      let count = 0;
      let tasksLength = 0;
      const tasks = await this.$store.dispatch('getJobTasks', {
        jobId: this.job._id,
        allTasks: true,
        return: true,
      });
      tasks.forEach((task) => {
        let value;
        if (task.taskType === 'folder') {
          return;
        }
        tasksLength++;
        if (typeof task.completenessPercentage === 'string') {
          if (task.completenessPercentage.includes('%')) {
            value = parseInt(task.completenessPercentage.split('%')[0], 10);
          } else {
            value = parseInt(task.completenessPercentage, 10);
          }
        } else {
          value = task.completenessPercentage;
        }
        count += value;
      });
      const percentageCompleteness = (count / tasksLength).toFixed(2);
      const newJob = { ...this.job };
      newJob.completenessPercentage = `${percentageCompleteness} %`;
      await this.$store.dispatch('updateJobSimple', newJob);
    },

    async updateThumbnail() {
      // First task inside the job
      let tasks;
      this.isLoading = true;
      try {
        tasks = await this.$store.dispatch('getJobTasks', {
          jobId: this.jobId,
          allTasks: true,
          return: true,
        });
      } catch (error) {
        this.isLoading = false;
        console.log('failed to get tasks');
        return false;
      }

      if (!tasks || tasks.length === 0) return false;
      const folders = tasks
        .filter((item) => item.taskType === 'folder')
        .sort(compareByTaskOrder);
      const tasksWithoutFolders = tasks
        .filter((item) => !item.folderId && item.taskType !== 'folder')
        .sort(compareByTaskOrder);

      if (tasksWithoutFolders.length > 0) {
        this.isLoading = false;
        return this.updateThumbnailAux(tasksWithoutFolders);
      }
      const tasksInsideFolder = tasks.filter(
        (item) => item.folderId === folders[0]._id
      );
      this.isLoading = false;
      return this.updateThumbnailAux(tasksInsideFolder);
    },
    async updateThumbnailAux(tasks) {
      let task;

      // Get the first diagram or table
      // If there are none, select the first task
      for (let i = 0; i < tasks.length; i++) {
        const item = tasks[i];
        if (item.taskType === 'diagram' || item.taskType === 'table') {
          task = item;
          break;
        }
      }
      if (!task) {
        [task] = tasks;
      }

      // Ignore if there are no tasks or if the only task is a folder
      if (!task) return false;
      if (task?.taskType === 'folder') return false;

      const newThumbnail = this.getTaskThumbnail(task);

      // Only save if there are changes
      if (newThumbnail !== this.job.thumbnailUrl) {
        const newJob = { ...this.job };

        newJob.thumbnailUrl = newThumbnail;

        await this.$store.dispatch('updateJobSimple', newJob);
      }
    },
    // Very ugly workaround untill we move the logic to use pdf on backend
    async handlePDF(file) {
      const pdf = await getDocument(URL.createObjectURL(file)).promise;
      const page = await pdf.getPage(1);
      const scale = 1;
      const viewport = page.getViewport({ scale });
      const canvas = document.createElement('canvas');
      const context = canvas.getContext('2d');
      canvas.height = viewport.height;
      canvas.width = viewport.width;
      const renderContext = {
        canvasContext: context,
        viewport,
      };
      await page.render(renderContext).promise;
      return canvas.toDataURL();
    },
  },
};
</script>

<style lang="scss" scoped>
.assembly-overview {
  display: flex;
  justify-content: center;
  align-items: center;
  &:hover {
    cursor: pointer;
  }
}

.sidebar-open {
  margin-right: 200px;
}

.task-container {
  font-family: 'Siemens Sans';
  max-width: 100%;
  margin: 0;
}

.task-img {
  width: auto;
  max-height: 100%;
  max-width: 100%;
  margin: auto;
}

.task-img-icon {
  position: absolute;
  top: 5%;
  left: 80%;
}

.img-icon {
  width: auto;
  max-height: 100%;
  max-width: 100%;
}

.thumbnail {
  position: relative;
}

.ghost {
  opacity: 0.5;
  background: #c8ebfb !important;
}

.list-group {
  display: contents;
}

.drag-list-group {
  display: flex;
  flex-wrap: wrap;
}

.modal-card-body {
  padding: 5px 20px;
  overflow: hidden;
}
.modal-card-head,
.modal-card-foot {
  border-radius: 0px;
}

.modal-card-foot {
  padding: 0;
  button {
    margin: 0;
    height: 50px;
    border: 1px solid #ccc;
    border-left: none;
    width: 50%;
    background-color: #f6f6f6;
    color: #888888;
    font-size: 16px;
    font-family: 'Siemens Roman';
    &:hover {
      cursor: pointer;
      font-weight: bold;
    }
  }
}
.edit-name-modal {
  .modal-card {
    width: 450px !important;
  }
}

.task-order {
  cursor: grab;
  .thumbnail {
    pointer-events: none !important;
  }
}
.breadcrumb {
  display: inherit;
}

.folder {
  background-color: #afafaf;
  color: #222;
}

.folder-clip-path {
  clip-path: polygon(0 0, 30% 0, 34% 10%, 100% 10%, 200% 110%, -100% 110%);
}

.folder-button {
  margin-right: 15px;
  padding: 40px;
  margin-top: 15px;
}

.selector {
  position: absolute;
  z-index: 1;
  &-input {
    width: 30px;
    height: 30px;
  }
}
/* ----------- iPad Pro ----------- */
/* Portrait and Landscape */
@media only screen and (min-width: 1024px) and (max-height: 1366px) and (-webkit-min-device-pixel-ratio: 1.5) {
}

/* Portrait */
@media only screen and (min-width: 1024px) and (max-height: 1366px) and (orientation: portrait) and (-webkit-min-device-pixel-ratio: 1.5) {
  /* For portrait layouts only */
  .task-items-outer .task-list {
    display: flex;
    flex-wrap: wrap;
    padding: 10px 30px;
    height: calc(100% - 100px);
  }

  .task-items-outer .task-item {
    display: block;
    width: 200px;
    height: 210px;
    margin: 0px 8px 30px 4px;
    color: #333;
    font-family: 'Siemens Sans';

    .thumbnail {
      width: 100%;
      height: 110px;
    }
  }

  .note-begin,
  .note-medium,
  .note-complete {
    height: 100px;

    .note-inner {
      .note-inner-task-name {
        max-width: 100%;
        max-height: none;
        white-space: normal;
      }
    }
  }

  .task-items-outer .task-item {
    height: 210px;
  }

  .note-inner-task-name {
    overflow: hidden;
    text-overflow: ellipsis;
    display: -webkit-box;
    -webkit-line-clamp: 2; /* number of lines to show */
    -webkit-box-orient: vertical;
  }
}

.completed-distance {
  padding-top: 41px !important;
}

/* Landscape */
@media only screen and (min-width: 1024px) and (max-height: 1366px) and (orientation: landscape) and (-webkit-min-device-pixel-ratio: 1.5) {
  /* For landscape layouts only */
  .task-item {
    width: 200px;

    .thumbnail {
      height: 110px;
    }
    .diagram-icons {
      margin-top: 15px;
    }
  }

  .task-items-outer .note-begin,
  .task-items-outer .note-medium,
  .task-items-outer .note-complete {
    height: 110px;
  }

  .task-items-outer .task-item {
    height: 210px;
    margin: 0px 25px 30px 4px;
  }

  .note-inner-task-name {
    overflow: hidden;
    text-overflow: ellipsis;
    display: -webkit-box;
    -webkit-line-clamp: 2; /* number of lines to show */
    -webkit-box-orient: vertical;
  }

  .task-list {
    display: -webkit-box;
    display: -ms-flexbox;
    display: flex;
    -ms-flex-wrap: wrap;
    flex-wrap: wrap;
    padding: 10px 46px 45px 40px;
    height: calc(100% - 100px);
    overflow-y: auto;
  }

  .note-begin,
  .note-medium,
  .note-complete {
    .note-inner {
      .note-inner-task-name {
        max-width: 100%;
        max-height: none;
        white-space: normal;
      }
    }
  }
}

@media only screen and (device-width: 768px) {
  /* For general iPad layouts */
}

@media only screen and (min-device-width: 481px) and (max-device-width: 1024px) and (orientation: portrait) {
  /* For portrait layouts only */
  .task-items-outer .task-list {
    display: flex;
    flex-wrap: wrap;
    padding: 10px 30px;
    height: calc(100% - 100px);
  }

  .task-items-outer .task-item {
    display: block;
    width: 200px;
    height: 210px;
    margin: 0px 8px 30px 4px;
    color: #333;
    font-family: 'Siemens Sans';

    .thumbnail {
      width: 100%;
      height: 110px;
    }
  }

  .note-begin,
  .note-medium,
  .note-complete {
    height: 100px;

    .note-inner {
      .note-inner-task-name {
        max-width: 100%;
        max-height: none;
        white-space: normal;
      }
    }
  }

  .task-items-outer .task-item {
    height: 210px;
  }

  .note-inner-task-name {
    overflow: hidden;
    text-overflow: ellipsis;
    display: -webkit-box;
    -webkit-line-clamp: 2; /* number of lines to show */
    -webkit-box-orient: vertical;
  }
}

.note-options {
  margin-left: auto;
}

@media only screen and (min-device-width: 481px) and (max-device-width: 1024px) and (orientation: landscape) {
  /* For landscape layouts only */
  .task-item {
    width: 200px;

    .thumbnail {
      height: 110px;
    }
    .diagram-icons {
      margin-top: 15px;
    }
  }

  .task-items-outer .note-begin,
  .task-items-outer .note-medium,
  .task-items-outer .note-complete {
    height: 110px;
  }

  .task-items-outer .task-item {
    height: 210px;
    margin: 0px 25px 30px 4px;
  }

  .note-inner-task-name {
    overflow: hidden;
    text-overflow: ellipsis;
    display: -webkit-box;
    -webkit-line-clamp: 2; /* number of lines to show */
    -webkit-box-orient: vertical;
  }

  .task-list {
    display: -webkit-box;
    display: -ms-flexbox;
    display: flex;
    -ms-flex-wrap: wrap;
    flex-wrap: wrap;
    padding: 10px 46px 45px 40px;
    height: calc(100% - 100px);
    overflow-y: auto;
  }

  .note-begin,
  .note-medium,
  .note-complete {
    .note-inner {
      .note-inner-task-name {
        max-width: 100%;
        max-height: none;
        white-space: normal;
      }
    }
  }
}

.floating-button {
  cursor: pointer;
  width: 60px;
  height: 60px;
  color: #7957d5;
  position: fixed;
  bottom: 100px; /* Bottom margin */
  right: 100px; /* Right margin */
  z-index: 1000; /* Ensure it floats above other components */
}

.tooltip {
  position: absolute;
  bottom: 70px; /* Adjust based on button size */
  right: -30px;
  background-color: #7957d5;
  color: #fff;
  padding: 8px;
  border-radius: 4px;
  font-size: 14px;
  white-space: nowrap;
  box-shadow: 0px 4px 6px rgba(0, 0, 0, 0.1);
  opacity: 0;
  transition: opacity 0.2s ease;
}

.tooltip::after {
  content: '';
  position: absolute;
  top: 100%;
  left: 70%;
  transform: translateX(-50%);
  border-width: 5px;
  border-style: solid;
  border-color: #7957d5 transparent transparent transparent;
}

.floating-button:hover .tooltip {
  opacity: 1; /* Show tooltip on hover */
}

.task-disabled {
  position: relative;
}

.task-disabled::after {
  content: '';
  position: absolute;
  inset: 0;
  background: rgba(255, 255, 255, 0.6);
  border: 1px dashed rgba(150, 150, 150, 0.5);
  z-index: 10;
  pointer-events: auto;
  cursor: not-allowed;
}

.disabled-overlay-trigger {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 11;
}

.loading-wrap {
  display: flex;
  flex-direction: column;
  align-items: center;
}
.loading-text {
  margin-top: 3.75rem;
}
</style>
